import ColorSpace from "./ColorSpace.js";
import toGamut from "./toGamut.js";
import { constrain } from "./angles.js";

/** @import { ColorSpace as ColorSpaceType } from "./types.js" */

// Cell count per coordinate: each keyed coordinate's range is divided into this many cells.
// Calibrated so a cell spans < 1e-4 of chroma for OKLCh + P3 (slopes measured by
// scripts/oklch-gamut-bounds.js); finer precision would not change the result.
const STEPS = 1e6;

// A specialized, memoized maxChroma function per (reduce space, gamut), built lazily on first use
// and keyed by their ids. Each closes over a two-layer digit trie of cached results: the lightness
// key descends the outer trie to a node whose `.h` holds a hue trie, descended in turn to a
// `.value` holding the max chroma. Each key drops its trailing zeros, so a round l or h costs no levels.
/** @typedef {Array<any> & { value?: number }} HueTrie A digit indexes into the next level; `.value` is the max chroma */
/** @typedef {Array<any> & { h?: HueTrie }} LightnessTrie A digit indexes into the next level; `.h` is the hue trie at this lightness */
/** @type {Record<string, Record<string, (l: number, h: number) => number>>} */
const cache = {};

// Significant digits of a ≤ 6-digit integer key (trailing zeros omitted, so round numbers stay shallow)
function digitsOf (n) {
	let digits = [];
	for (let div = STEPS / 10; n; div /= 10) {
		digits.push(Math.floor(n / div));
		n %= div;
	}
	return digits;
}

/**
 * Maximum chroma within an RGB gamut for a given lightness and hue, in a polar space's chroma units.
 * @param {number} l Lightness, in the reduce space's units
 * @param {number} h Hue angle, in degrees
 * @param {{ space: string | ColorSpaceType, gamut: string | ColorSpaceType }} options
 *        `space` is the polar space whose chroma is reduced; `gamut` is the RGB gamut to fit.
 * @returns {number}
 */
export default function maxChroma (l, h, { space, gamut }) {
	space = ColorSpace.get(space);
	gamut = ColorSpace.get(gamut);

	let fn = ((cache[space.id] ??= {})[gamut.id] ??= makeMaxChroma(space, gamut));
	return fn(l, h);
}

/**
 * Build the specialized maxChroma function for one (space, gamut) pair, doing the per-pair setup
 * (validation, role identification, ranges) once and capturing it in the returned closure.
 * @param {ColorSpaceType} space Resolved polar space whose chroma is reduced
 * @param {ColorSpaceType} gamut Resolved RGB gamut to fit
 * @returns {(l: number, h: number) => number}
 */
function makeMaxChroma (space, gamut) {
	if (!space.isPolar || !("c" in space.coords)) {
		throw new TypeError(
			`maxChroma() needs a polar space with a "c" coordinate, got ${space.id}`,
		);
	}

	// Identify coordinates by role
	let ids = Object.keys(space.coords);
	let hueId = ids.find(id => space.coords[id].type === "angle");
	let lightnessId = ids.find(id => id !== "c" && id !== hueId);
	let range = id => space.coords[id].range ?? space.coords[id].refRange;
	let [lMin, lMax] = range(lightnessId);
	let [hMin, hMax] = range(hueId);
	let cMax = range("c")[1];
	let cIndex = ids.indexOf("c");
	let method = `${space.id}.c`;
	let root = /** @type {LightnessTrie} */ ([]);

	return function (l, h) {
		if (l <= lMin || l >= lMax) {
			return 0;
		}

		let lNode = root;
		for (let d of digitsOf(Math.floor(((l - lMin) / (lMax - lMin)) * STEPS))) {
			lNode = lNode[d] ??= [];
		}

		let hNode = (lNode.h ??= []);
		for (let d of digitsOf(Math.floor(((constrain(h) - hMin) / (hMax - hMin)) * STEPS))) {
			hNode = hNode[d] ??= [];
		}

		let coords = /** @type {[number, number, number]} */ (
			ids.map(id => (id === "c" ? cMax : id === hueId ? h : l))
		);

		return (hNode.value ??= toGamut(
			{ space, coords },
			// deltaE OK stays sensitive near black, where deltaE 2000 is too forgiving of excess chroma
			{ space: gamut, method, deltaEMethod: "OK", jnd: 0 },
		).coords[cIndex]);
	};
}
